/**
 * BulkEditConfirmationModal
 *
 * Confirmation modal shown when a cell is focused with >= BULK_CONFIRM_THRESHOLD
 * selected rows. Lets the user choose to propagate edits to all selected rows
 * or restrict to the focused row only.
 *
 * "Edit this row only" ends the selection, so it goes through the shared
 * `clearSelection` — the one writer that also returns `scope` to `'visible'`
 * (SYD-2386).
 */

import { FC, memo, useContext } from 'react';
import { Modal, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import { clearSelection } from '@/workflows/runtime/arTableSelection';

const { Text } = Typography;

const BulkEditConfirmationModal: FC = () => {
  const uiState = useContext(ArTableUIStateContext)!;
  const snap = useSnapshot(uiState.bulkEditPreview);
  const pending = snap.pendingConfirmation;

  const handleConfirm = () => {
    const p = uiState.bulkEditPreview.pendingConfirmation;
    if (!p) return;

    uiState.bulkEditPreview.byColumnId[p.columnId] = {
      sourceRowId: p.rowId,
      targetRows: p.targetRows,
    };
    uiState.bulkEditPreview.pendingConfirmation = null;
  };

  const handleCancel = () => {
    uiState.bulkEditPreview.pendingConfirmation = null;

    clearSelection(uiState.selection);
  };

  return (
    <Modal
      aria-label="artable-bulk-edit-confirmation-modal"
      title="Bulk Edit"
      open={pending !== null}
      destroyOnHidden
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText="Continue"
      cancelText="Edit this row only"
    >
      <Text>
        This edit will apply to {Object.keys(pending?.targetRows ?? {}).length} selected
        rows. Continue?
      </Text>
    </Modal>
  );
};

export default memo(BulkEditConfirmationModal);
